import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IMPORT_MODES,
  LANGUAGE_LABELS,
  PREFERRED_LANGUAGE_OPTIONS,
  type ImportMode,
  type IndexerUsageView,
  type PreferredLanguage,
  type QualityProfileDTO,
  type SettingsUpdate,
  type SettingsView,
  type TestResult,
} from '@jelly-dl/shared';
import { api, ApiError } from '../api/client';
import { Button, Card, ErrorState, Field, Input, Spinner, Toggle } from '../components/ui';
import { formatDateTime } from '../lib/format';

type Target = 'indexer' | 'downloader' | 'metadata';

const selectClass =
  'w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-indigo-500';

export function Settings() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsView>('/api/settings'),
  });
  const { data: profiles } = useQuery({
    queryKey: ['quality-profiles'],
    queryFn: () => api.get<{ profiles: QualityProfileDTO[] }>('/api/quality-profiles'),
  });
  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get<IndexerUsageView>('/api/usage'),
    refetchInterval: 15_000,
  });

  const [form, setForm] = useState<Partial<SettingsUpdate>>({});
  const [secrets, setSecrets] = useState<{ indexerApiKey: string; nzbgetPass: string; tmdbApiKey: string }>({
    indexerApiKey: '',
    nzbgetPass: '',
    tmdbApiKey: '',
  });
  const [saved, setSaved] = useState('');
  const [saveError, setSaveError] = useState('');
  const [tests, setTests] = useState<Partial<Record<Target, TestResult>>>({});

  useEffect(() => {
    if (!data) return;
    setForm({
      indexerEnabled: data.indexerEnabled,
      indexerBaseUrl: data.indexerBaseUrl,
      indexerCategories: data.indexerCategories,
      indexerHourlyApiCap: data.indexerHourlyApiCap,
      indexerMinIntervalMs: data.indexerMinIntervalMs,
      nzbgetUrl: data.nzbgetUrl,
      nzbgetUser: data.nzbgetUser,
      nzbgetCategory: data.nzbgetCategory,
      downloadsPath: data.downloadsPath,
      nzbgetPathMapFrom: data.nzbgetPathMapFrom,
      nzbgetPathMapTo: data.nzbgetPathMapTo,
      outputMoviesPath: data.outputMoviesPath,
      outputShowsPath: data.outputShowsPath,
      importMode: data.importMode,
      importDryRun: data.importDryRun,
      metadataUserAgentContact: data.metadataUserAgentContact,
      preferredLanguage: data.preferredLanguage,
      allowEnglishFallback: data.allowEnglishFallback,
      maxSizeGb: data.maxSizeGb,
      searchEnabled: data.searchEnabled,
      maxConcurrentDownloads: data.maxConcurrentDownloads,
      seasonPackBeforeEpisodes: data.seasonPackBeforeEpisodes,
      metadataRefreshHours: data.metadataRefreshHours,
      removeCompletedDownloads: data.removeCompletedDownloads,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const body: SettingsUpdate = { ...form };
      if (secrets.indexerApiKey) body.indexerApiKey = secrets.indexerApiKey;
      if (secrets.nzbgetPass) body.nzbgetPass = secrets.nzbgetPass;
      if (secrets.tmdbApiKey) body.tmdbApiKey = secrets.tmdbApiKey;
      return api.patch<SettingsView>('/api/settings', body);
    },
    onSuccess: () => {
      setSaved('Saved');
      setSaveError('');
      setSecrets({ indexerApiKey: '', nzbgetPass: '', tmdbApiKey: '' });
      qc.invalidateQueries({ queryKey: ['settings'] });
      setTimeout(() => setSaved(''), 2000);
    },
    onError: (e) => setSaveError(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const test = useMutation({
    mutationFn: (target: Target) => api.post<TestResult>(`/api/settings/test/${target}`),
    onSuccess: (res, target) => setTests((t) => ({ ...t, [target]: res })),
    onError: (e, target) =>
      setTests((t) => ({ ...t, [target]: { ok: false, message: e instanceof ApiError ? e.message : 'failed' } })),
  });

  if (isLoading) return <Spinner />;
  if (isError || !data) return <ErrorState message="Couldn't load settings." onRetry={() => refetch()} />;

  const set = <K extends keyof SettingsUpdate>(k: K, v: SettingsUpdate[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Settings</h1>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-emerald-400">{saved}</span>}
          {saveError && <span className="text-sm text-rose-400">{saveError}</span>}
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {/* Indexer usage dashboard */}
      {usage.data && (
        <Card className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <Stat label="API calls this hour" value={`${usage.data.localApiCount} / ${usage.data.hourlyApiCap}`} />
          <Stat
            label="Server API budget"
            value={usage.data.apiMax != null ? `${usage.data.apiCurrent ?? '?'} / ${usage.data.apiMax}` : '—'}
          />
          <Stat label="Grabs this hour" value={String(usage.data.localGrabCount)} />
          <Stat
            label="Backoff"
            value={
              usage.data.backoffUntil && usage.data.backoffUntil > Date.now()
                ? `until ${formatDateTime(usage.data.backoffUntil)}`
                : 'none'
            }
          />
        </Card>
      )}

      {/* Indexer */}
      <Card className="space-y-3">
        <SectionHeader title="Indexer — scenenzbs" onTest={() => test.mutate('indexer')} result={tests.indexer} />
        <Row>
          <Field label="Enabled">
            <Toggle checked={!!form.indexerEnabled} onChange={(v) => set('indexerEnabled', v)} />
          </Field>
          <Field label="Base URL">
            <Input value={form.indexerBaseUrl ?? ''} onChange={(e) => set('indexerBaseUrl', e.target.value)} />
          </Field>
        </Row>
        <Field label="API key" hint={data.indexerApiKeySet ? 'A key is set — leave blank to keep it' : 'Required'}>
          <Input
            type="password"
            placeholder={data.indexerApiKeySet ? '•••••••• (set)' : 'paste API key'}
            value={secrets.indexerApiKey}
            onChange={(e) => setSecrets((s) => ({ ...s, indexerApiKey: e.target.value }))}
          />
        </Field>
        <Row>
          <Field label="Categories" hint="Newznab category ids (comma-separated)">
            <Input value={form.indexerCategories ?? ''} onChange={(e) => set('indexerCategories', e.target.value)} />
          </Field>
          <Field label="Hourly API cap" hint="Stay under your indexer's limit (e.g. 400)">
            <NumInput value={form.indexerHourlyApiCap ?? 0} min={1} onChange={(v) => set('indexerHourlyApiCap', v)} />
          </Field>
          <Field label="Min interval (ms)">
            <NumInput value={form.indexerMinIntervalMs ?? 0} min={250} onChange={(v) => set('indexerMinIntervalMs', v)} />
          </Field>
        </Row>
      </Card>

      {/* Downloader */}
      <Card className="space-y-3">
        <SectionHeader title="Downloader — NZBGet" onTest={() => test.mutate('downloader')} result={tests.downloader} />
        <Row>
          <Field label="URL">
            <Input value={form.nzbgetUrl ?? ''} onChange={(e) => set('nzbgetUrl', e.target.value)} />
          </Field>
          <Field label="Username">
            <Input value={form.nzbgetUser ?? ''} onChange={(e) => set('nzbgetUser', e.target.value)} />
          </Field>
          <Field label="Password" hint={data.nzbgetPassSet ? 'set — blank keeps it' : ''}>
            <Input
              type="password"
              placeholder={data.nzbgetPassSet ? '•••••••• (set)' : ''}
              value={secrets.nzbgetPass}
              onChange={(e) => setSecrets((s) => ({ ...s, nzbgetPass: e.target.value }))}
            />
          </Field>
        </Row>
        <Row>
          <Field label="Category">
            <Input value={form.nzbgetCategory ?? ''} onChange={(e) => set('nzbgetCategory', e.target.value)} />
          </Field>
          <Field label="Downloads path (in app container)">
            <Input value={form.downloadsPath ?? ''} onChange={(e) => set('downloadsPath', e.target.value)} />
          </Field>
        </Row>
        <Row>
          <Field label="Path map: from (NZBGet path)" hint="Optional — if NZBGet reports a different prefix">
            <Input value={form.nzbgetPathMapFrom ?? ''} onChange={(e) => set('nzbgetPathMapFrom', e.target.value)} />
          </Field>
          <Field label="Path map: to (app path)">
            <Input value={form.nzbgetPathMapTo ?? ''} onChange={(e) => set('nzbgetPathMapTo', e.target.value)} />
          </Field>
        </Row>
      </Card>

      {/* Output — Jellyfin library on disk */}
      <Card className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-300">Output — Jellyfin library</h2>
        <Row>
          <Field label="Movies path" hint="Where imported movies are placed">
            <Input value={form.outputMoviesPath ?? ''} onChange={(e) => set('outputMoviesPath', e.target.value)} />
          </Field>
          <Field label="Shows path" hint="Where imported TV episodes are placed">
            <Input value={form.outputShowsPath ?? ''} onChange={(e) => set('outputShowsPath', e.target.value)} />
          </Field>
        </Row>
        <Row>
          <Field label="Import mode" hint="hardlink keeps the download seedable; copy duplicates the file">
            <select
              className={selectClass}
              value={form.importMode ?? 'hardlink'}
              onChange={(e) => set('importMode', e.target.value as ImportMode)}
            >
              {IMPORT_MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Dry run" hint="Plan imports without moving files">
            <Toggle checked={!!form.importDryRun} onChange={(v) => set('importDryRun', v)} />
          </Field>
        </Row>
      </Card>

      {/* Metadata — TMDB */}
      <Card className="space-y-3">
        <SectionHeader title="Metadata — TMDB" onTest={() => test.mutate('metadata')} result={tests.metadata} />
        <Field label="TMDB API key" hint={data.tmdbApiKeySet ? 'set — blank keeps it' : 'Required for search & metadata'}>
          <Input
            type="password"
            placeholder={data.tmdbApiKeySet ? '•••••••• (set)' : 'paste TMDB API key'}
            value={secrets.tmdbApiKey}
            onChange={(e) => setSecrets((s) => ({ ...s, tmdbApiKey: e.target.value }))}
          />
        </Field>
        <Field label="Contact (User-Agent)" hint="A URL or email sent with metadata requests">
          <Input
            value={form.metadataUserAgentContact ?? ''}
            onChange={(e) => set('metadataUserAgentContact', e.target.value)}
          />
        </Field>
      </Card>

      {/* Content filter */}
      <Card className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-300">Content filter</h2>
        <Row>
          <Field label="Preferred language">
            <select
              className={selectClass}
              value={form.preferredLanguage ?? 'any'}
              onChange={(e) => set('preferredLanguage', e.target.value as PreferredLanguage)}
            >
              {PREFERRED_LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === 'any' ? 'Any (no preference)' : LANGUAGE_LABELS[opt]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Allow English fallback"
            hint="Prefer the chosen language, but grab another language if none is found. Turn off to only ever grab the chosen language."
          >
            <Toggle
              checked={!!form.allowEnglishFallback}
              onChange={(v) => set('allowEnglishFallback', v)}
              label="Allow English fallback"
            />
          </Field>
          <Field
            label="Max size (GB)"
            hint="Skip releases larger than this in standard (non-4K) mode. 0 = no limit. The per-title 4K toggle ignores this."
          >
            <NumInput value={form.maxSizeGb ?? 0} min={0} onChange={(v) => set('maxSizeGb', v)} />
          </Field>
        </Row>
      </Card>

      {/* Automation */}
      <Card className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-300">Automation</h2>
        <Row>
          <Field label="Auto-search enabled">
            <Toggle checked={!!form.searchEnabled} onChange={(v) => set('searchEnabled', v)} />
          </Field>
          <Field label="Max concurrent downloads">
            <NumInput value={form.maxConcurrentDownloads ?? 1} min={1} onChange={(v) => set('maxConcurrentDownloads', v)} />
          </Field>
          <Field label="Metadata refresh (hours)">
            <NumInput value={form.metadataRefreshHours ?? 24} min={1} onChange={(v) => set('metadataRefreshHours', v)} />
          </Field>
        </Row>
        <Row>
          <Field label="Prefer season packs before episodes">
            <Toggle
              checked={!!form.seasonPackBeforeEpisodes}
              onChange={(v) => set('seasonPackBeforeEpisodes', v)}
            />
          </Field>
          <Field label="Clean NZBGet history after import">
            <Toggle checked={!!form.removeCompletedDownloads} onChange={(v) => set('removeCompletedDownloads', v)} />
          </Field>
        </Row>
      </Card>

      {/* Provider info */}
      <Card className="space-y-2 text-sm text-slate-400">
        <h2 className="text-sm font-semibold text-slate-300">Usenet provider</h2>
        <p>
          Your Usenet provider credentials (host / port / SSL / username / password) are configured directly in the
          bundled <span className="text-slate-200">NZBGet</span> container via the{' '}
          <code className="text-slate-200">NZB_SERVER_*</code> environment variables in{' '}
          <code className="text-slate-200">.env</code>. This app talks to NZBGet, not to your provider directly.
        </p>
      </Card>

      {/* Quality profiles */}
      <Card className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-300">Quality profiles</h2>
        {profiles?.profiles.map((p) => (
          <div key={p.id} className="text-sm text-slate-300">
            <span className="font-medium">{p.name}</span> — cutoff {p.cutoff}, allows{' '}
            {p.items
              .filter((i) => i.allowed)
              .map((i) => i.quality)
              .join(', ')}
          </div>
        ))}
      </Card>
    </div>
  );
}

function SectionHeader({ title, onTest, result }: { title: string; onTest: () => void; result?: TestResult }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-slate-300">{title}</h2>
      <div className="flex items-center gap-3">
        {result && (
          <span className={result.ok ? 'text-xs text-emerald-400' : 'text-xs text-rose-400'}>{result.message}</span>
        )}
        <Button variant="ghost" onClick={onTest}>
          Test
        </Button>
      </div>
    </div>
  );
}

function NumInput({ value, onChange, min }: { value: number; onChange: (n: number) => void; min?: number }) {
  // Keep an editable string so clearing the field to retype it doesn't immediately coerce to
  // 0/NaN; only propagate a valid finite number (>= min) up to the form.
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText(String(value));
  }, [value]);
  return (
    <Input
      type="number"
      min={min}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value !== '' && Number.isFinite(n) && (min == null || n >= min)) onChange(n);
      }}
    />
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-base font-medium text-slate-100">{value}</div>
    </div>
  );
}
